import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, GearSixIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { type FieldErrors, FormProvider, type Resolver, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetPackagesSettings } from '@/api/packages/get-packages-settings'
import { useRegeneratePackageRoomUrl } from '@/api/packages/regenerate-package-room-url'
import { useUpdatePackagesSettings } from '@/api/packages/update-packages-settings'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { NavbarMenu, NavbarMenuItem } from '@/components/ui/navbar-menu'
import { toastFactory } from '@/factories'
import { useFormSave } from '@/hooks'
import type { PackageSettings } from '@/models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { getApiErrorMessage } from '@/utils'

import { CarriersTab } from './carriers-tab'
import { GeneralTab } from './general-tab'
import { LocationsTab } from './locations-tab'
import { PackageRoomTab } from './package-room-tab'
import * as S from './packages-settings.styled'
import {
  makePackagesSettingsFormSchema,
  type PackagesSettingsForm,
  type PackagesSettingsProps,
  type TabKey,
} from './packages-settings.types'
import { getTabOptions, mapSettingsToForm } from './packages-settings.utils'

type PackagesSettingsEditorProps = PackagesSettingsProps & {
  settings: PackageSettings
}

const PackagesSettingsEditor = ({
  onClose,
  buildingId,
  isPackagesD2DAvailable,
  isQuickScanAvailable,
  settings,
}: PackagesSettingsEditorProps) => {
  const { t } = useTranslation(['packages-settings'])
  const selectedBuilding = useSelector(selectActiveBuilding)
  const { mutateAsync: updateSettings, isPending: isSaving } = useUpdatePackagesSettings(buildingId)
  const { mutateAsync: regenerateUrl, isPending: isRegenerating } =
    useRegeneratePackageRoomUrl(buildingId)

  const [activeTab, setActiveTab] = useState<TabKey>('general')

  const form = useForm<PackagesSettingsForm>({
    mode: 'onTouched',
    resolver: zodResolver(makePackagesSettingsFormSchema(t)) as Resolver<PackagesSettingsForm>,
    values: mapSettingsToForm(settings),
  })

  const handleSave = async (formData: PackagesSettingsForm) => {
    const payload = {
      allowASAP: formData.allowASAP,
      allowQuickScan: formData.allowQuickScan,
      buildingId,
      businessHours: formData.businessHours
        .filter((h) => h.enabled)
        .map(({ name, startTime, endTime }) => ({ endTime, name, startTime })),
      carrierAccessCodeEnabled: formData.carrierAccessCodeEnabled,
      carriers: formData.carriers
        .filter((carrier) => carrier.name.trim() !== '')
        .map((carrier) => carrier.name),
      d2dMode: formData.d2dMode,
      deliverySlotLength: formData.deliverySlotLength,
      locationRequired: formData.locationRequired,
      locations: formData.customLocationsEnabled
        ? formData.locations
            .filter((location) => location.name.trim() !== '')
            .map(({ name }) => ({ name }))
        : [],
      maskUsernameEnabled: formData.maskUsernameEnabled,
      maxDeliveriesPerSlot: formData.maxDeliveriesPerSlot,
      photoRequired: formData.photoRequired,
      trackingNumberRequired: formData.trackingNumberRequired,
    }

    try {
      await updateSettings(payload as unknown as Partial<PackageSettings>)
      toastFactory.success(t('packages-settings:modal.toastSaveSuccess'))
      onClose()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleErrors = (errors: FieldErrors<PackagesSettingsForm>) => {
    if (errors.locations || errors.locationRequired) {
      setActiveTab('locations')
    } else if (errors.carriers) {
      setActiveTab('carriers')
    }
  }

  const save = useFormSave({
    form,
    onInvalid: handleErrors,
    onSubmit: handleSave,
    onUnchanged: onClose,
  })

  const handleCopyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      toastFactory.success(t('packages-settings:packageRoomTab.toastCopySuccess'))
    } catch {
      toastFactory.error(t('packages-settings:packageRoomTab.toastCopyError'))
    }
  }

  const handleRegenerateLink = async (packageRoomId?: number) => {
    try {
      await regenerateUrl({ buildingId, packageRoomId })
      toastFactory.success(t('packages-settings:packageRoomTab.toastRegenerateSuccess'))
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const packageRoomAvailable = selectedBuilding?.packageRoomAvailable ?? false
  const tabOptions = getTabOptions(t, packageRoomAvailable)

  return (
    <FormProvider {...form}>
      <Modal
        autoWidth
        title={t('packages-settings:modal.title')}
        headerIcon={GearSixIcon}
        bodyPaddingTop="1.6rem"
        footer={
          <>
            <Button use="secondary" disabled={isRegenerating} onClick={onClose}>
              {t('packages-settings:modal.close')}
            </Button>
            <Button use="primary" loading={isSaving} disabled={isRegenerating} onClick={save}>
              {t('packages-settings:modal.save')}
              <CheckIcon />
            </Button>
          </>
        }
        onClose={onClose}
      >
        <S.Body>
          <NavbarMenu>
            {tabOptions.map((tab) => (
              <NavbarMenuItem
                key={tab.value}
                active={activeTab === tab.value}
                onClick={() => setActiveTab(tab.value)}
              >
                {tab.label}
              </NavbarMenuItem>
            ))}
          </NavbarMenu>

          <S.TabContent>
            {activeTab === 'general' && (
              <GeneralTab
                isD2DAvailable={isPackagesD2DAvailable}
                isQuickScanAvailable={isQuickScanAvailable}
              />
            )}

            {activeTab === 'package-room' && (
              <PackageRoomTab
                settings={settings}
                isRegenerating={isRegenerating}
                onCopyToClipboard={handleCopyToClipboard}
                onRegenerateLink={handleRegenerateLink}
              />
            )}

            {activeTab === 'locations' && (
              <LocationsTab packageRoomAvailable={packageRoomAvailable} />
            )}

            {activeTab === 'carriers' && <CarriersTab />}
          </S.TabContent>
        </S.Body>
      </Modal>
    </FormProvider>
  )
}

export const PackagesSettings = ({ buildingId, ...props }: PackagesSettingsProps) => {
  const { data: settings } = useGetPackagesSettings({ buildingId })

  if (!settings) return null

  return (
    <PackagesSettingsEditor
      key={buildingId}
      settings={settings}
      buildingId={buildingId}
      {...props}
    />
  )
}
